import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import Helmet from 'helmet/helmet';

import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import Spinner from 'four-nets/ui/spinner/spinner';

import defaultStyles from './fetch-more-button.scss';

export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
    styles: typeof defaultStyles;
    loading?: boolean;
    href?: string;
    prevHref?: string;
}

export const adjacentHrefsFromCursor = (params: { endCursor?: string | null; pathname: string; pageSize: number }) => {
    const { endCursor, pathname, pageSize } = params;

    let endCursorHref, prevCursorHref;
    if (endCursor) {
        const nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
        endCursorHref = `${pathname}?offset=${nextOffset}`;
        if (nextOffset - pageSize * 2 >= 0) {
            prevCursorHref = `${pathname}?offset=${nextOffset - pageSize * 2}`;
        }
    }
    return [endCursorHref, prevCursorHref];
};

const FetchMoreButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
    (props: Props, ref: React.Ref<HTMLButtonElement | HTMLAnchorElement>) => {
        const { styles, loading, disabled, href, prevHref, onClick, ...rest } = props;

        const inner = (
            <span className={styles.container}>
                <FormattedMessage id='BUTTON_LOAD_MORE' />
                {loading ? <Spinner style={{ spinner: styles.spinner }} mini={true} /> : null}
            </span>
        );

        const achorOnClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            event.stopPropagation();

            if (onClick) {
                onClick(event);
            }
        };

        const renderHelmet = (H: React.ComponentType) => {
            return (
                <H>
                    {prevHref ? <link rel='prev' href={prevHref} /> : null}
                    <link rel='next' href={href} />
                </H>
            );
        };

        return href ? (
            <React.Fragment>
                <Helmet render={renderHelmet} />
                <a
                    href={href}
                    onClick={achorOnClick}
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className={styles.button}
                    {...rest}
                >
                    {inner}
                </a>
            </React.Fragment>
        ) : (
            <button
                onClick={onClick}
                ref={ref as React.Ref<HTMLButtonElement>}
                className={styles.button}
                disabled={loading || disabled}
                {...rest}
            >
                {inner}
            </button>
        );
    }
);

export default Styled(defaultStyles)(FetchMoreButton);
